import React, { useEffect, useState } from 'react';
import { usersAPI, publicAPI, componentsAPI, conversationsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { formatLocalDate } from '../services/date';

const UsersDirectory = () => {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [sendToUserId, setSendToUserId] = useState(null);
  const [sendMessage, setSendMessage] = useState('');
  // removed unused sendRecipeId
  // Clone modal state
  const [showClone, setShowClone] = useState(false);
  const [cloneSource, setCloneSource] = useState({ userId: null, recipeId: null, recipe: null });
  const [myComponents, setMyComponents] = useState([]);
  const [cloneSelects, setCloneSelects] = useState({ brass: '', bullet: '', primer: '', powder: '' });

  const search = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.listVisible(q);
      const list = Array.isArray(res.data) ? res.data : [];
      let meId = undefined;
      try { meId = JSON.parse(localStorage.getItem('authUser') || '{}')?.id; } catch {}
      setUsers(list.filter(u => u.id !== meId));
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openUser = async (userId) => {
    try {
      setSelectedRecipe(null);
      const res = await publicAPI.getUser(userId);
      setSelectedUser(res.data);
    } catch (e) {
      toast.error('Failed to load user');
    }
  };

  const openRecipe = async (userId, recipeId) => {
    try {
      const res = await publicAPI.getRecipe(userId, recipeId);
      setSelectedRecipe(res.data);
    } catch (e) {
      toast.error('Failed to load recipe');
    }
  };

  const openCloneModal = async (userId, recipeId) => {
    try {
      const res = await publicAPI.getRecipe(userId, recipeId);
      const recipe = res.data;
      const mineRes = await componentsAPI.getAll();
      const mine = Array.isArray(mineRes.data) ? mineRes.data : [];
      setMyComponents(mine);
      setCloneSource({ userId, recipeId, recipe });
      setCloneSelects({ brass: '', bullet: '', primer: '', powder: '' });
      setShowClone(true);
    } catch (e) {
      toast.error('Failed to prepare clone');
    }
  };

  const filteredOptions = (type) => {
    const lower = (s) => (s || '').toString().trim().toLowerCase();
    const comp = (cloneSource.recipe?.components || []).find(rc => rc.type === type)?.component || {};
    const list = myComponents.filter(c => lower(c.component_type?.name) === lower(type));
    if (type === 'brass') {
      const cart = lower(comp.caliber);
      return list.filter(c => lower(c.caliber) === cart);
    }
    if (type === 'bullet') {
      const srcCi = parseFloat(comp.caliber_inches || '0');
      return list.filter(c => Math.abs(parseFloat(c.caliber_inches || '0') - srcCi) < 0.0005);
    }
    if (type === 'primer') {
      return list.filter(c => lower(c.firearm_type) === lower(comp.firearm_type) && lower(c.primer_size) === lower(comp.primer_size));
    }
    return list; // powder
  };

  const confirmClone = async () => {
    try {
      const map = {};
      Object.entries(cloneSelects).forEach(([k, v]) => { if (v) map[k] = Number(v); });
      await publicAPI.cloneRecipe(cloneSource.userId, cloneSource.recipeId, map);
      toast.success('Recipe cloned to your library');
      setShowClone(false);
    } catch (e) {
      const backendMsg = e?.response?.data?.message || e?.response?.data?.error;
      if (backendMsg) {
        toast.error(backendMsg);
      } else {
        toast.error('Failed to clone recipe');
      }
    }
  };

  const sendMsg = async () => {
    try {
      if (!sendToUserId || !sendMessage.trim()) return;
      const up = await conversationsAPI.upsert([sendToUserId]);
      const convId = up?.data?.id;
      if (!convId) throw new Error('Failed to start chat');
      await conversationsAPI.sendMessage(convId, sendMessage.trim(), undefined);
      toast.success('Message sent');
      setSendToUserId(null);
      setSendMessage('');
      // removed unused sendRecipeId
    } catch (e) {
      toast.error('Failed to send message');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { search(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <input className="input" placeholder="Search by name, display name, or email" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-secondary" onClick={search}>Search</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 card p-4">
          <h2 className="text-lg font-semibold mb-3">Visible Users</h2>
          {loading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : (
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.id} className="p-3 border rounded cursor-pointer hover:bg-gray-50" onClick={() => openUser(u.id)}>
                  <div className="font-medium">{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </div>
              ))}
              {users.length === 0 && <div className="text-sm text-gray-500">No users</div>}
            </div>
          )}
        </div>

        <div className="md:col-span-2 card p-4">
          {selectedUser ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">{selectedUser.display_name || `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() || selectedUser.email}</h2>
                  <div className="text-sm text-gray-500">{selectedUser.email}</div>
                </div>
                <button className="btn btn-secondary" onClick={() => { setSendToUserId(selectedUser.id); setSendMessage(''); }}>Message</button>
              </div>

              <div>
                <h3 className="font-medium mb-2">Active Calibers</h3>
                <div className="flex flex-wrap gap-2">
                  {(selectedUser.active_calibers || []).map((c,i) => (
                    <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{c}</span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Owned Firearms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(selectedUser.firearms || []).map(f => (
                    <div key={f.id} className="p-2 border rounded text-sm">
                      {f.manufacturer} {f.model} ({f.caliber})
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Recipes</h3>
                <div className="space-y-2">
                  {(selectedUser.recipes || []).map(r => (
                    <div key={r.id} className="p-3 border rounded">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-gray-500">{r.cartridge || '—'}</div>
                        </div>
                        <div className="space-x-2">
                          <button className="btn btn-sm btn-secondary" onClick={() => openRecipe(selectedUser.id, r.id)}>Details</button>
                          {/* Hide clone if viewing own user */}
                          {selectedUser.id !== (JSON.parse(localStorage.getItem('authUser') || '{}')?.id) && (
                            <button className="btn btn-sm btn-primary" onClick={() => openCloneModal(selectedUser.id, r.id)}>Clone</button>
                          )}
                          {/* Remove share from directory; sharing will be via Recipes tab modal */}
                        </div>
                      </div>
                      {selectedRecipe && selectedRecipe.id === r.id && (
                        <PublicRecipeModal recipe={selectedRecipe} onClose={() => setSelectedRecipe(null)} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">Select a user to view details</div>
          )}
        </div>
      </div>

      {/* Send Message Modal */}
      {sendToUserId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-3">Send Message</h3>
            <textarea className="input h-28" placeholder="Write your message…" value={sendMessage} onChange={(e) => setSendMessage(e.target.value)} />
            <div className="flex justify-end gap-2 mt-3">
              <button className="btn" onClick={() => { setSendToUserId(null); setSendMessage(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={sendMsg}>Send</button>
            </div>
          </div>
        </div>
      )}
      {showClone && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-3">Clone Recipe - Map Components</h3>
            <p className="text-sm text-gray-600 mb-4">Select your existing components to use. Leave empty to import from the source recipe.</p>
            {['brass','bullet','primer','powder'].map((t) => (
              <div key={t} className="mb-3">
                <label className="label capitalize">{t}</label>
                <select className="input" value={cloneSelects[t]} onChange={(e)=>setCloneSelects(s => ({...s, [t]: e.target.value}))}>
                  <option value="">Import from source</option>
                  {filteredOptions(t).map(c => (
                    <option key={c.id} value={c.id}>{c.brand} {c.model}</option>
                  ))}
                </select>
              </div>
            ))}
            <div className="flex justify-end gap-2 mt-4">
              <button className="btn" onClick={()=>setShowClone(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmClone}>Clone</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal to show full public recipe details
const PublicRecipeModal = ({ recipe, onClose }) => {
  const typeColorClass = (typeName) => {
    const t = (typeName || '').toLowerCase();
    if (t === 'bullet') return 'text-blue-600';
    if (t === 'primer') return 'text-red-600';
    if (t === 'powder') return 'text-orange-600';
    if (t === 'brass') return 'text-yellow-600';
    return 'text-gray-500';
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">{recipe.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl leading-none p-1" aria-label="Close">×</button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <div className="text-gray-600">Cartridge</div>
            <div className="font-medium">{recipe.cartridge || '—'}</div>
          </div>
          <div>
            <div className="text-gray-600">Created</div>
            <div className="font-medium">{recipe.created_at ? formatLocalDate(recipe.created_at) : '—'}</div>
          </div>
        </div>
        {(recipe.case_trim_length || recipe.cartridge_overall_length || recipe.seating_depth || recipe.crimp_type || recipe.crimp_strength) && (
          <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
            {recipe.case_trim_length && (
              <div>
                <div className="text-gray-600">Case Trim Length</div>
                <div className="font-medium">{recipe.case_trim_length}"</div>
              </div>
            )}
            {recipe.cartridge_overall_length && (
              <div>
                <div className="text-gray-600">Overall Length</div>
                <div className="font-medium">{recipe.cartridge_overall_length}"</div>
              </div>
            )}
            {recipe.seating_depth && (
              <div>
                <div className="text-gray-600">Seating Depth</div>
                <div className="font-medium">{recipe.seating_depth}"</div>
              </div>
            )}
            {recipe.crimp_type && (
              <div>
                <div className="text-gray-600">Crimp Type</div>
                <div className="font-medium">{recipe.crimp_type}</div>
              </div>
            )}
            {typeof recipe.crimp_strength !== 'undefined' && recipe.crimp_strength !== null && (
              <div>
                <div className="text-gray-600">Crimp Strength</div>
                <div className="font-medium">{recipe.crimp_strength}</div>
              </div>
            )}
          </div>
        )}
        {/* Self-cannelure is shown inside bullet section below */}
        {recipe.description && (
          <div className="mb-4">
            <div className="text-gray-600 text-sm">Description</div>
            <div className="text-sm">{recipe.description}</div>
          </div>
        )}
        {recipe.notes && (
          <div className="mb-4">
            <div className="text-gray-600 text-sm">Notes</div>
            <div className="text-sm">{recipe.notes}</div>
          </div>
        )}
        <div className="mb-4">
          <div className="text-sm font-medium text-gray-700 mb-2">Components</div>
          <div className="space-y-2">
            {(recipe.components || []).map((rc, idx) => {
              const c = rc.component || {};
              return (
                <div key={idx} className="p-3 bg-gray-50 rounded text-sm">
                  <div className="flex justify-between">
                    <div>
                      <div className="font-medium">{c.brand} {c.model}</div>
                      <div className={`text-xs uppercase tracking-wide font-medium ${typeColorClass(rc.type)}`}>{rc.type}</div>
                    </div>
                    <div className="text-right text-gray-700">{rc.quantity} {rc.unit}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                    {c.caliber && <div>Caliber: {c.caliber}</div>}
                    {c.description && <div className="col-span-2">{c.description}</div>}
                    {c.head_stamp && <div>Head Stamp: {c.head_stamp}</div>}
                    {c.firearm_type && <div>Firearm Type: {c.firearm_type}</div>}
                    {c.primer_size && <div>Primer Size: {c.primer_size}</div>}
                    {typeof c.magnum !== 'undefined' && <div>Magnum: {c.magnum ? 'Yes' : 'No'}</div>}
                    {c.weight_grains && <div>Weight: {c.weight_grains} gr</div>}
                    {c.length_inches && <div>Length: {c.length_inches}"</div>}
                    {c.caliber_inches && <div>Bullet Caliber: {c.caliber_inches}"</div>}
                    {rc.type?.toLowerCase() === 'bullet' && (
                      <>
                        {(() => {
                          const selfCan = !!recipe.self_cannelured;
                          const hasCannelure = !!c.cannelured;
                          if (!selfCan && !hasCannelure) return null;
                          const depth = hasCannelure ? c.cannelure_depth : recipe.cannelure_depth;
                          return (
                            <div className="col-span-2">Cannelure: {selfCan ? 'Self-cannelured' : 'Factory'}{typeof depth === 'number' ? ` • Depth ${depth}"` : ''}</div>
                          );
                        })()}
                      </>
                    )}
                    {c.core_material && <div>Core: {c.core_material}</div>}
                    {c.covering && <div>Covering: {c.covering}</div>}
                    {c.shape && <div>Shape: {c.shape}</div>}
                    {c.intended_cartridges && <div className="col-span-2">Intended: {c.intended_cartridges}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">Velocity by Firearm</div>
          {(recipe.velocity_by_firearm || []).length > 0 ? (
            <div className="space-y-1 text-xs text-gray-700">
              {recipe.velocity_by_firearm.map((vs, i) => (
                <div key={i}>{vs.manufacturer} {vs.model}: Avg {vs.average}, ES {vs.extreme_spread}, SD {vs.standard_deviation}, N {vs.count}</div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-gray-500">No velocity stats available</div>
          )}
        </div>
        <div className="mt-4">
          <div className="text-sm font-medium text-gray-700 mb-1">Owner Avg Cost per Load</div>
          <div className="text-sm">{recipe.owner_average_cost_per_load != null ? `$${Number(recipe.owner_average_cost_per_load).toFixed(2)}` : 'NA'}</div>
        </div>
      </div>
    </div>
  );
};

export default UsersDirectory;